import React, { useEffect } from 'react'
import { m } from 'framer-motion'
import { MapPin, Clock, PhoneCall, Train, type LucideIcon } from 'lucide-react'
import Navigation from '../Restaurant/components/Navigation'
import Footer from '../Restaurant/components/Footer'
import StickyCTA from '../Restaurant/components/StickyCTA'
import LocalIntentSection from '../Restaurant/components/LocalIntentSection'
import { useCMS } from '../../context/CMSContext'
import { useLanguage } from '../../context/LanguageContext'
import { useTheme } from '../../context/ThemeContext'
import { getImageUrl } from '../../utils/imageUtils'
import { PHONE_DISPLAY, PHONE_LINK, NAVER_MAP_URL } from '../../constants/contact'
import { trackCtaClick } from '../../utils/analytics'
import '../../styles/components/restaurant.css'

interface LocationCard {
    icon: LucideIcon
    title: string
    value: string
    detail: string
    href?: string
    cta?: string
}

const LocationPage: React.FC = () => {
    const { content } = useCMS()
    const { language } = useLanguage()
    const { theme } = useTheme()

    useEffect(() => {
        window.scrollTo({ top: 0 })
    }, [])

    const headerImage = getImageUrl('/images/naver_1762994586149_hero.webp')

    const heading =
        language === 'ko'
            ? {
                  eyebrow: 'Location',
                  title: '오시는 길',
                  lead: '서울 송파구 가락시장. 흑돈1931로 찾아오시는 가장 쉬운 길을 안내합니다.',
              }
            : {
                  eyebrow: 'Location',
                  title: 'How to Find Us',
                  lead: 'Garak Market, Songpa-gu, Seoul — here is the easiest way to reach BlackPork1931.',
              }

    const cards: LocationCard[] =
        language === 'ko'
            ? [
                  {
                      icon: MapPin,
                      title: '주소',
                      value: '서울특별시 송파구 송파대로30길 41-26 1층',
                      detail: '가락시장 인근 · 지도에서 위치 보기',
                      href: NAVER_MAP_URL,
                      cta: '네이버 지도 →',
                  },
                  {
                      icon: Clock,
                      title: '영업시간',
                      value: '매일 11:30 – 22:00',
                      detail: '브레이크타임 15:00 – 17:00 · 연중무휴',
                  },
                  {
                      icon: PhoneCall,
                      title: '전화 · 예약',
                      value: PHONE_DISPLAY,
                      detail: '전화 문의 및 네이버 예약 가능',
                      href: PHONE_LINK,
                      cta: '전화 걸기 →',
                  },
                  {
                      icon: Train,
                      title: '대중교통',
                      value: '3·8호선 가락시장역, 8호선 경찰병원역 인근',
                      detail: '가락시장 도보권 · 자세한 주차 안내는 아래 참고',
                  },
              ]
            : [
                  {
                      icon: MapPin,
                      title: 'Address',
                      value: '41-26 Songpa-daero 30-gil, Songpa-gu, Seoul (1F)',
                      detail: 'Near Garak Market · view on map',
                      href: NAVER_MAP_URL,
                      cta: 'Naver Map →',
                  },
                  {
                      icon: Clock,
                      title: 'Hours',
                      value: 'Daily 11:30 – 22:00',
                      detail: 'Break time 15:00 – 17:00 · open year-round',
                  },
                  {
                      icon: PhoneCall,
                      title: 'Phone · Reservation',
                      value: PHONE_DISPLAY,
                      detail: 'Call or reserve via Naver',
                      href: PHONE_LINK,
                      cta: 'Call now →',
                  },
                  {
                      icon: Train,
                      title: 'Transit',
                      value: 'Near Garak Market Stn. (Line 3·8), Police Hospital Stn. (Line 8)',
                      detail: 'Walking distance from Garak Market · see parking below',
                  },
              ]

    return (
        <div className={`restaurant-page location-page theme-${theme.mode}`}>
            <Navigation />

            <header className="story-hero">
                <div className="story-hero-bg" style={{ backgroundImage: `url(${headerImage})` }} />
                <div className="story-hero-overlay" />
                <m.div
                    className="story-hero-content"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <span className="story-eyebrow">{heading.eyebrow}</span>
                    <h1 className="story-hero-title">{heading.title}</h1>
                    <p className="story-hero-lead">{heading.lead}</p>
                </m.div>
            </header>

            <section className={`story-section theme-${theme.mode}`}>
                <div className="story-wrap">
                    <span className="story-eyebrow story-eyebrow-dark">{language === 'ko' ? '매장 안내' : 'Store Info'}</span>
                    <h2 className="story-section-title">{language === 'ko' ? '한눈에 보는 방문 정보' : 'Everything You Need to Visit'}</h2>
                    <div className="story-breeds">
                        {cards.map((card, i) => {
                            const Icon = card.icon
                            const inner = (
                                <>
                                    <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Icon size={18} />
                                        {card.title}
                                    </h3>
                                    <span className="story-breed-origin">{card.value}</span>
                                    <p>{card.detail}</p>
                                    {card.cta && <span className="story-breed-origin" style={{ color: '#dc2626', marginTop: 8 }}>{card.cta}</span>}
                                </>
                            )
                            return card.href ? (
                                <m.a
                                    key={i}
                                    href={card.href}
                                    target={card.href.startsWith('http') ? '_blank' : undefined}
                                    rel={card.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                    className="story-breed"
                                    style={{ textDecoration: 'none' }}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    onClick={() => trackCtaClick(card.href === PHONE_LINK ? 'call' : 'map', 'location_page')}
                                >
                                    {inner}
                                </m.a>
                            ) : (
                                <m.div
                                    key={i}
                                    className="story-breed"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                >
                                    {inner}
                                </m.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            <LocalIntentSection />

            {content.footer && <Footer content={content.footer} />}
            <StickyCTA />
        </div>
    )
}

export default LocationPage
